import React from 'react';

import { Grid, ToggleButton, ToggleButtonGroup, alpha, useTheme } from '@mui/material';

import type {
  CampaignMode,
  StatusCampaignPayloadBuilderState,
} from '~/utils/societePayloadBuilder/statusCampaign/types';
import type { OffersHubMode, WhatsNewCarouselMode } from '~/utils/societePayloadBuilder/types';

import StatusCampaignPayloadActions from './StatusCampaign/StatusCampaignPayloadActions';

export type PayloadBuilderMode = CampaignMode | OffersHubMode | WhatsNewCarouselMode;

export const STANDALONE_PAYLOAD_MODES = new Set<PayloadBuilderMode>(['offersHub', 'whatsNewCarousel']);

export function isStandalonePayloadMode(mode: PayloadBuilderMode): boolean {
  return STANDALONE_PAYLOAD_MODES.has(mode);
}

interface PayloadBuilderToolbarProps {
  selectedMode: PayloadBuilderMode;
  state: StatusCampaignPayloadBuilderState;
  isValid: boolean;
  standaloneActions?: React.ReactNode;
  onRequestModeChange: (mode: PayloadBuilderMode) => void;
}

export default function PayloadBuilderToolbar({
  selectedMode,
  state,
  isValid,
  standaloneActions,
  onRequestModeChange,
}: PayloadBuilderToolbarProps) {
  const theme = useTheme();

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, value: PayloadBuilderMode | null) => {
    if (!value || value === selectedMode) return;
    onRequestModeChange(value);
  };

  const toggleGroupSx = {
    gap: 1,
    bgcolor: 'transparent',
    '& .MuiToggleButtonGroup-grouped': {
      flex: 1,
      borderRadius: '4px !important',
      border: '1px solid transparent !important',
      marginLeft: '0 !important',
      textTransform: 'none',
      fontWeight: 600,
      color: 'text.secondary',
      '&:not(:first-of-type)': {
        marginLeft: `${theme.spacing(1)} !important`,
        borderLeft: '1px solid transparent !important',
      },
      '&.Mui-selected': {
        color: 'primary.main',
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        borderColor: `${alpha(theme.palette.primary.main, 0.24)} !important`,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.12),
        },
      },
      '&:not(.Mui-selected):hover': {
        bgcolor: alpha(theme.palette.action.hover, 0.04),
      },
    },
  } as const;

  return (
    <Grid container alignItems="center" spacing={1}>
      <Grid size={{ xs: 12, md: 5.4 }} sx={{ py: 0.5, pr: { md: 2 } }}>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={selectedMode}
          onChange={handleModeChange}
          color="primary"
          size="small"
          sx={toggleGroupSx}
        >
          <ToggleButton value="statusMatch">Status Match</ToggleButton>
          <ToggleButton value="statusClaim">Status Claim</ToggleButton>
          <ToggleButton value="offersHub">Offers Hub</ToggleButton>
          <ToggleButton value="whatsNewCarousel">What&apos;s New Carousel</ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      <Grid
        size={{ xs: 12, md: 6.6 }}
        sx={{
          py: 0.5,
          display: isStandalonePayloadMode(selectedMode) ? 'flex' : { xs: 'none', md: 'flex' },
          justifyContent: 'flex-end',
          minWidth: 0,
        }}
      >
        {standaloneActions ??
          (isStandalonePayloadMode(selectedMode) ? null : (
            <StatusCampaignPayloadActions state={state} isValid={isValid} />
          ))}
      </Grid>
    </Grid>
  );
}
